import { useCallback, useEffect, useRef, useState } from 'react';

import { findReusableInclusionAsset } from './inclusionsModel';
import type { InclusionAsset, InclusionAssetLink, InclusionDraft, InclusionFormValues, InclusionsDraft } from './types';

export function useInclusionDrafts(offerId: string | undefined) {
  const [drafts, setDrafts] = useState<Array<InclusionDraft>>([]);
  const [inclusionAssets, setInclusionAssets] = useState<Array<InclusionAsset>>([]);
  // Mirrors inclusionAssets so several links made in one tick reuse the same new asset.
  const inclusionAssetsRef = useRef<Array<InclusionAsset>>([]);

  const replaceInclusionAssets = useCallback((assets: Array<InclusionAsset>) => {
    inclusionAssetsRef.current = assets;
    setInclusionAssets(assets);
  }, []);

  useEffect(() => {
    setDrafts([]);
    replaceInclusionAssets([]);
  }, [offerId, replaceInclusionAssets]);

  // See docs/business-rules.md#inclusion-assets.
  const linkAssetByName = useCallback(
    (name: string, accountAssets: Array<InclusionAsset> = []): InclusionAssetLink | undefined => {
      const trimmed = name.trim();
      if (!trimmed) {
        return undefined;
      }
      const asset = findReusableInclusionAsset(trimmed, inclusionAssetsRef.current, accountAssets) ?? {
        id: crypto.randomUUID(),
        name: trimmed,
      };
      if (!inclusionAssetsRef.current.some((existing) => existing.id === asset.id) && !asset.sfid) {
        replaceInclusionAssets([...inclusionAssetsRef.current, asset]);
      }
      return { assetId: asset.id, assetName: asset.name };
    },
    [replaceInclusionAssets],
  );

  // An explicit create always makes a new asset; the form has already ruled out duplicates.
  const createInclusionAsset = useCallback(
    (name: string): InclusionAsset => {
      const asset = { id: crypto.randomUUID(), name: name.trim() };
      replaceInclusionAssets([...inclusionAssetsRef.current, asset]);
      return asset;
    },
    [replaceInclusionAssets],
  );

  const addDraft = useCallback((values: InclusionFormValues) => {
    const now = new Date().toISOString();
    const draft: InclusionDraft = { ...values, id: crypto.randomUUID(), createdAt: now, updatedAt: now };
    setDrafts((prevDrafts) => [...prevDrafts, draft]);
    return draft;
  }, []);

  const updateDraft = useCallback((id: string, changes: Partial<InclusionFormValues>) => {
    setDrafts((prevDrafts) =>
      prevDrafts.map((draft) =>
        draft.id === id ? { ...draft, ...changes, updatedAt: new Date().toISOString() } : draft,
      ),
    );
  }, []);

  const removeDraft = useCallback((id: string) => {
    setDrafts((prevDrafts) => prevDrafts.filter((draft) => draft.id !== id));
  }, []);

  const replaceOptionDrafts = useCallback((pricingOptionIndex: number, sourceDrafts: Array<InclusionFormValues>) => {
    const now = new Date().toISOString();
    const clones: Array<InclusionDraft> = sourceDrafts.map((draft) => ({
      ...draft,
      id: crypto.randomUUID(),
      pricingOptionIndex,
      createdAt: now,
      updatedAt: now,
    }));
    setDrafts((prevDrafts) => [
      ...prevDrafts.filter((draft) => draft.pricingOptionIndex !== pricingOptionIndex),
      ...clones,
    ]);
  }, []);

  const loadDraft = useCallback(
    (saved: InclusionsDraft) => {
      const now = new Date().toISOString();
      setDrafts(saved.drafts.map((draft) => ({ ...draft, createdAt: now, updatedAt: now })));
      replaceInclusionAssets(saved.inclusionAssets);
    },
    [replaceInclusionAssets],
  );

  return {
    drafts,
    inclusionAssets,
    addDraft,
    updateDraft,
    removeDraft,
    replaceOptionDrafts,
    linkAssetByName,
    createInclusionAsset,
    loadDraft,
  };
}
